// makeDeployment.js
import { encrypt } from "../utils/crypt";
import { log } from "../utils/logger";

function escapeBadChars(encryptedStr) {
    if (!encryptedStr) return encryptedStr;
    
    // Escape ALL non-alphanumeric characters except safe ones
    // Only allow: letters (a-z, A-Z), numbers (0-9), and safe chars (. _ -)
    return encryptedStr.replace(/[^a-zA-Z0-9._-]/g, match => {
        return `&#${match.charCodeAt(0)};`;
    });
}

async function refreshStoredAccessToken(vectorVault) {
    if (!vectorVault || typeof vectorVault.refreshAccessToken !== 'function') {
        throw new Error('Session expired. Please log in again.');
    }

    const storedAccessToken = localStorage.getItem('vectorVaultAccessToken');
    const storedRefreshToken = localStorage.getItem('vectorVaultRefreshToken');

    if (storedAccessToken && typeof vectorVault.setAccessToken === 'function') {
        vectorVault.setAccessToken(storedAccessToken);
    }

    if (storedRefreshToken && typeof vectorVault.setRefreshToken === 'function') {
        vectorVault.setRefreshToken(storedRefreshToken);
    }

    const refreshSuccess = await vectorVault.refreshAccessToken();

    if (!refreshSuccess) {
        throw new Error('Session expired. Please log in again.');
    }

    const accessToken = vectorVault.getAccessToken();
    const refreshToken = vectorVault.getRefreshToken();

    if (!accessToken) {
        throw new Error('Session expired. Please log in again.');
    }

    localStorage.setItem('vectorVaultAccessToken', accessToken);

    if (refreshToken) {
        localStorage.setItem('vectorVaultRefreshToken', refreshToken);
    }

    return accessToken;
}

async function readDeployError(response) {
    const errorText = await response.text();

    try {
        return JSON.parse(errorText);
    } catch (error) {
        return errorText;
    }
}
  
async function sendDeployRequest(
    user, 
    title, 
    vault, 
    forwardSlash, 
    chat, 
    data, 
    edit, 
    del, 
    model, 
    dark, 
    context, 
    description, 
    jsn,
    isPublic = true,
    users = [],
    conv = false,
    convFlow = '',
    vectorVault = null
) {
    const url = "https://deploy.vectorvault.io/deploy"
    const userRaw = encrypt(user);
    console.log('Raw encrypted user:', userRaw);
    const userEncrypted = escapeBadChars(userRaw);
    console.log('Escaped user:', userEncrypted);
    
    // Create base payload
    const payload = {
        env_vars: {
            USER: userEncrypted,
            TITLE: title,
            DESCRIPTION: description,
            FORWARDSLASH: forwardSlash,
            VAULT: vault,
            CHAT: chat,
            DATA: data,
            CONV: conv,
            CONV_FLOW: convFlow,
            EDIT: edit,
            DELETE: del,
            MODEL: model,
            DARK: dark,
            CONTEXT: context,
            JSON: jsn,
            // Add new authentication params
            IS_PUBLIC: isPublic,
        }
    };
    
    // Add users array if not public
    if (!isPublic && Array.isArray(users) && users.length > 0) {
        // Filter out any incomplete user entries
        const validUsers = users.filter(user => user.username && user.password);
        
        if (validUsers.length > 0) {
            // Convert users array to format expected by server
            // Using a simple encoding for now - the server will need to handle this format
            payload.env_vars.USERS = JSON.stringify(validUsers);
        }
    }

    try {

        log('Deployment request payload:', payload);
        const buildRequestOptions = (token) => ({
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(payload)
        });

        const token = localStorage.getItem('vectorVaultAccessToken');
        let response = await fetch(url, buildRequestOptions(token));

        if (response.status === 401) {
            const refreshedToken = await refreshStoredAccessToken(vectorVault);
            response = await fetch(url, buildRequestOptions(refreshedToken));

            if (response.status === 401) {
                throw new Error('Session expired. Please log in again.');
            }
        }

        if (!response.ok) {
            const errorDetails = await readDeployError(response);
            const errorMessage = typeof errorDetails === 'string'
                ? errorDetails
                : errorDetails.message || errorDetails.error || errorDetails.error_code || JSON.stringify(errorDetails);
            throw new Error('Server responded with an error: ' + errorMessage);
        }
    
        const responseData = await response.json();
        return responseData;
    } catch (error) {
        console.error("Error in POST request:", error);
        throw error; // Re-throw the error so it can be handled by the caller
    }
}

export default sendDeployRequest;

// // Example usage
// sendDeployRequest(
//     "john.rood@decision.com",
//     "Johns Awesome :)",
//     "hey",
//     "your_forward_slash",
//     true,  // chat
//     true,  // data
//     false, // edit
//     false, // delete
//     "gpt-4o",
//     true,  // dark
//     false, // context
//     "This vector database contains The Prince",
//     false, // json
//     false, // Not public
//     [{ username: "admin", password: "secure123" }], // User credentials
//     false, // conversation
//     ""     // conversation flow
// ).then(response => {
//     console.log("Response:", response);
// });